"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getForwardedReadDates = exports.getPreferredDate = exports.useEstimateInsights = exports.getEstimationStates = exports.getPeriodLatestReadDates = exports.getLatestActualDate = void 0;
var date_fns_1 = require("date-fns");
var react_1 = __importDefault(require("react"));
var core_1 = require("../../../shared/core");
var copy_1 = require("../copy");
/**
 * Look for any actual intermediate reads as the closing read is not always an
 * actual one but an intermediate read is.
 */
var getLatestIntermediateActualDate = function (reads) {
    return reads.reduce(function (prevDate, read) {
        var isIntermediateActual = read.readType !== 'EstimatedReading';
        if (isIntermediateActual && prevDate && (0, date_fns_1.isAfter)(read.date, prevDate)) {
            return read.date;
        }
        if (isIntermediateActual && !prevDate) {
            return read.date;
        }
        return prevDate;
    }, null);
};
/**
 * Go through the rates from a billing period, find the latest actual read date.
 */
var getLatestActualDate = function (rates) {
    return rates.reduce(function (prevDate, rate) {
        var isClosingActual = rate.closingReadType !== 'EstimatedReading';
        if (isClosingActual && prevDate && (0, date_fns_1.isAfter)(rate.endDate, prevDate)) {
            return rate.endDate;
        }
        if (isClosingActual && !prevDate) {
            return rate.endDate;
        }
        var intermediateActualDate = getLatestIntermediateActualDate(rate.intermediateReads);
        if (intermediateActualDate &&
            prevDate &&
            (0, date_fns_1.isAfter)(intermediateActualDate, prevDate)) {
            return intermediateActualDate;
        }
        if (intermediateActualDate && !prevDate) {
            return intermediateActualDate;
        }
        return prevDate;
    }, null);
};
exports.getLatestActualDate = getLatestActualDate;
var hasRates = function (fuel) { return fuel.consumption.rates.length; };
var getPeriodLatestReadDates = function (_a) {
    var electricity = _a.electricity, gas = _a.gas;
    return ({
        electricity: electricity && hasRates(electricity)
            ? (0, exports.getLatestActualDate)(electricity.consumption.rates)
            : null,
        gas: gas && hasRates(gas) ? (0, exports.getLatestActualDate)(gas.consumption.rates) : null,
    });
};
exports.getPeriodLatestReadDates = getPeriodLatestReadDates;
/**
 * Returns an estimation state based on that last actual read.
 * 'accurate' with a read on the period end date.
 * 'estimated-accurate' with a read within 5 days of the end date.
 * 'estimated' if there is no read or the read is outside the 5 day window.
 */
var getEstimationState = function (readDate, periodEnd) {
    if (!readDate) {
        return 'estimate';
    }
    if ((0, date_fns_1.isSameDay)(readDate, periodEnd)) {
        return 'actual';
    }
    if ((0, date_fns_1.isAfter)(readDate, (0, date_fns_1.subDays)(periodEnd, 5))) {
        return 'accurate-estimate';
    }
    return 'estimate';
};
var getEstimationStates = function (dates, period) { return ({
    electricity: period.electricity && hasRates(period.electricity)
        ? getEstimationState(dates.electricity, period.end)
        : null,
    gas: period.gas && hasRates(period.gas)
        ? getEstimationState(dates.gas, period.end)
        : null,
}); };
exports.getEstimationStates = getEstimationStates;
/**
 * Returns the correct insights based on the estimation state. The logic is a
 * little tricky around dual fuel when the states are the same or differ.
 */
var useEstimateInsights = function (_a) {
    var electricity = _a.electricity, gas = _a.gas;
    var Copy = (0, core_1.useCopy)(copy_1.defaultOptions).Copy;
    var insights = [];
    if (electricity && gas) {
        if (electricity === gas) {
            // Fuel states are the same, use generic label.
            insights.push(electricity === 'actual'
                ? { label: react_1.default.createElement(Copy, { copyKey: "actualLabel" }), status: 'success' }
                : electricity === 'accurate-estimate'
                    ? {
                        label: react_1.default.createElement(Copy, { copyKey: "accurateEstimateLabel" }),
                        status: 'success',
                    }
                    : {
                        label: react_1.default.createElement(Copy, { copyKey: "estimateLabel", props: { fuel: 'both' } }),
                        status: 'warning',
                    });
            return insights;
        }
        // Fuel states differ, use fuel specific labels.
        insights.push(electricity === 'actual'
            ? { label: react_1.default.createElement(Copy, { copyKey: "actualElecLabel" }), status: 'success' }
            : electricity === 'accurate-estimate'
                ? {
                    label: react_1.default.createElement(Copy, { copyKey: "accurateEstimateElecLabel" }),
                    status: 'success',
                }
                : { label: react_1.default.createElement(Copy, { copyKey: "estimateElecLabel" }), status: 'warning' });
        insights.push(gas === 'actual'
            ? { label: react_1.default.createElement(Copy, { copyKey: "actualGasLabel" }), status: 'success' }
            : gas === 'accurate-estimate'
                ? {
                    label: react_1.default.createElement(Copy, { copyKey: "accurateEstimateGasLabel" }),
                    status: 'success',
                }
                : { label: react_1.default.createElement(Copy, { copyKey: "estimateGasLabel" }), status: 'warning' });
        return insights;
    }
    // Single fuel, use generic label
    electricity &&
        insights.push(electricity === 'actual'
            ? { label: react_1.default.createElement(Copy, { copyKey: "actualLabel" }), status: 'success' }
            : electricity === 'accurate-estimate'
                ? { label: react_1.default.createElement(Copy, { copyKey: "accurateEstimateLabel" }), status: 'success' }
                : {
                    label: (react_1.default.createElement(Copy, { copyKey: "estimateLabel", props: { fuel: 'electricity' } })),
                    status: 'warning',
                });
    gas &&
        insights.push(gas === 'actual'
            ? { label: react_1.default.createElement(Copy, { copyKey: "actualLabel" }), status: 'success' }
            : gas === 'accurate-estimate'
                ? { label: react_1.default.createElement(Copy, { copyKey: "accurateEstimateLabel" }), status: 'success' }
                : {
                    label: react_1.default.createElement(Copy, { copyKey: "estimateLabel", props: { fuel: 'gas' } }),
                    status: 'warning',
                });
    return insights;
};
exports.useEstimateInsights = useEstimateInsights;
/**
 * Returns the preferred date to use. If there is a current and a previous, we
 * prefer to use the previous date if the current one is not in the window (and
 * therefor would mean reads are estimated.)
 */
var getPreferredDate = function (current, previous, periodEnd) {
    if (current && previous) {
        return (0, date_fns_1.isAfter)(current, (0, date_fns_1.subDays)(periodEnd, 5)) ? current : previous;
    }
    return current || previous;
};
exports.getPreferredDate = getPreferredDate;
/**
 * Returns the date of the last actual read in the period. If the current period
 * as no actual read, we check the previous one as that indicates a bill can be
 * accurately estimated.
 */
var getForwardedReadDates = function (periods) {
    return periods.reduce(function (dates, period, index) {
        var previousPeriodState = index > 0 ? dates[index - 1] : { electricity: null, gas: null };
        var currentPeriodDates = (0, exports.getPeriodLatestReadDates)(period);
        var state = {
            electricity: (0, exports.getPreferredDate)(currentPeriodDates.electricity, previousPeriodState.electricity, period.end),
            gas: (0, exports.getPreferredDate)(currentPeriodDates.gas, previousPeriodState.gas, period.end),
        };
        dates.push(state);
        return dates;
    }, []);
};
exports.getForwardedReadDates = getForwardedReadDates;
